import { useNavigate, useParams } from "react-router-dom"
import { RequestStatus, User, useRequestLoaders, UserRole, useSystemActions, useSystemStateActions } from "../../../../modules/system"
import { Emitter, Event, useInitEmitter } from "../../../event-emitter"
import { DynamoSystemApi, useServicesHooks } from "../../../../services"
import { getInitialRoute, roleName } from "../../../roles"
import { TenantKey, UniqueKey } from "../../../../tables/constants"
import { useBranchLoaders, useManagerStateActions } from "../../../../modules/manager"
import { useCategoryLoaders, useProductLoaders, useProductsActions } from "../../../../modules/products"
import { usePurchasesActions } from "../../../../modules/purchases"
import { useCustomerTypeLoaders } from "../../../../modules/sales"
import { useStockLoaders, useUnitTypeLoaders } from "../../../../modules/stock"
import { Employee, useAdministrationStateActions, usePaymentTypeLoaders, useCashFundLoaders, useCreditCardLoaders, useEmployeeLoaders, useTaxLoaders, useSettingsLoaders, useExpenseTypeLoaders, useCheckLoaders } from "../../../../modules/administration"
import { useRoutesHooks } from "../../hooks/routesHooks"
import { RoutePath } from "../../types"
import { dedupe, firstItem } from "../../../../utils/listUtils"
import { useDefaultDbActions } from "../../../../modules/common"
import { useServer } from "../../../../services/server"
import { useDriverLoaders, useVehicleLoaders } from "../../../../modules/logistics"
import { TableName } from "../../../../tables"
import { useLocalStorageActions } from "../../../ui"
import { env } from "../../../../services/constants"
import { toLocalUrl } from "../../../files"

export const useLoad = () => {
    const { companyCode } = useParams<{ companyCode: string }>()
    const { loadUser, parseUrl } = useRoutesHooks()
    const { pathParts } = parseUrl()
    
    const { initUserApis } = useServicesHooks()
    const navigate = useNavigate()

    const { getAllBranches } = useServer()
    const { setCurrentEmployee } = useAdministrationStateActions()
    const { setCurrentBranch } = useManagerStateActions()
    const { setCompanyLogoUrl, setUserRequest } = useSystemStateActions()
    const { removeLoadedTablesData } = useLocalStorageActions()

    const branchLoaders = useBranchLoaders()
    const cashFundLoaders = useCashFundLoaders()
    const categoryLoaders = useCategoryLoaders()
    const checkLoaders = useCheckLoaders()
    const creditCardLoaders = useCreditCardLoaders()
    const customerTypeLoaders = useCustomerTypeLoaders()
    const driverLoaders = useDriverLoaders()
    const employeeLoaders = useEmployeeLoaders()
    const expenseTypeLoaders = useExpenseTypeLoaders()
    const paymentTypeLoaders = usePaymentTypeLoaders()
    const productLoaders = useProductLoaders()
    const requestLoaders = useRequestLoaders()
    const settingsLoaders = useSettingsLoaders()
    const stockLoaders = useStockLoaders()
    const taxLoaders = useTaxLoaders()
    const unitTypeLoaders = useUnitTypeLoaders()
    const vehicleLoaders = useVehicleLoaders()

    const defaultDbActions = useDefaultDbActions()
    const { updateCompanyUsers } = useSystemStateActions()
    const productsActions = useProductsActions()
    const purchasesActions = usePurchasesActions()
    const systemActions = useSystemActions()

    useInitEmitter()

    return async () => {
        const { fetchAllCompanies, fetchCompanyByCode } = systemActions()

        const code = companyCode!
        const companies = await fetchAllCompanies()
        const company = companies.find(company => company.code === code)

        if (company) {
            removeLoadedTablesData()
            const user = await loadUser()

            if (user)  {
                await fetchCompanyByCode(code, true)
                if (company.logoUrl) {
                    const logoUrl = await toLocalUrl(company.logoUrl)
                    setCompanyLogoUrl(logoUrl)
                }

                const { loadAllRequests, loadUserRequests } = requestLoaders(company.id)
                const requests = await loadUserRequests(user.id)
                const request = firstItem(requests)
                request && setUserRequest(request)

                if (request && request.status === RequestStatus.APPROVED && request.companyId) {
                    const companyId = request.companyId
                    
                    await getAllBranches(companyId, user.id)
                    console.log('env:', env)
                    
                    await initUserApis(roleName(code, request.role), user.email!, TenantKey, UniqueKey)

                    const { loadAllBranches } = branchLoaders(companyId)
                    const { loadAllCashFunds } = cashFundLoaders(companyId)
                    const { loadAllCategories } = categoryLoaders(companyId)
                    const { loadAllChecks } = checkLoaders(companyId)
                    const { loadAllCreditCards } = creditCardLoaders(companyId)
                    const { loadAllCustomerTypes } = customerTypeLoaders(companyId)
                    const { loadAllDrivers } = driverLoaders(companyId)
                    const { loadAllEmployees } = employeeLoaders(companyId)
                    const { loadAllExpenseTypes } = expenseTypeLoaders(companyId)   
                    const { loadAllPaymentTypes } = paymentTypeLoaders(companyId)
                    const { loadAllProducts } = productLoaders(companyId)
                    const { loadAllSettings } = settingsLoaders(companyId)
                    const { loadAllStock } = stockLoaders(companyId)
                    const { loadAllTaxes } = taxLoaders(companyId)
                    const { loadAllUnitTypes } = unitTypeLoaders()
                    const { loadAllVehicles } = vehicleLoaders(companyId)
                    
                    const { fetchAllProductPrices, fetchAllLots } = productsActions(companyId)
                    const { fetchAllSuppliers } = purchasesActions(companyId)

                    const branches = await loadAllBranches()
                    const employees = await loadAllEmployees()
                    await loadAllCashFunds()
                    await loadAllCategories()
                    await loadAllChecks()
                    await loadAllCreditCards()
                    await loadAllCustomerTypes()
                    await loadAllDrivers()
                    await loadAllExpenseTypes()
                    await loadAllPaymentTypes()
                    await loadAllProducts()
                    await loadAllSettings()
                    await loadAllStock()
                    await loadAllTaxes()
                    await loadAllUnitTypes()
                    await loadAllVehicles()

                    const employee = employees.find(employee => employee.email === user.email)
                    if (employee) {
                        setCurrentEmployee(employee as Employee)
                        const employeeBranch = branches.find(branch => branch.id === employee.branchId)!
                        setCurrentBranch(employeeBranch)
                    } else {
                        setCurrentBranch(firstItem(branches)!)
                        Emitter.emit(Event.EMPLOYEE_MISSING, request)
                    }                    

                    await fetchAllLots()
                    await fetchAllSuppliers()
                    await fetchAllProductPrices()
                    if (request.role === UserRole.MANAGER) {
                        const requests = await loadAllRequests()
                        const { getByIds } = defaultDbActions(DynamoSystemApi)
                        const userIds = dedupe(requests.map(request => request.userId))
                        const users = await getByIds(TableName.USERS, userIds) as User[]
                        updateCompanyUsers(users)
                    } 

                    if (pathParts.length === 1) {
                        const initialRoute = getInitialRoute(request.role)
                        navigate(initialRoute)
                    }
                } else {
                    navigate(RoutePath.ACCESS)
                }
            }
        } else {
            navigate(RoutePath.ERROR)
        }   
    }
}
